import {
  createContext,
  CSSProperties,
  Fragment,
  memo,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, useFloating } from '@floating-ui/react-dom';

import { formatDecimalValuation } from '@libs/chess/formatValuation';
import { IEvaluationScore, IPgnVariantDto, IPgnMove } from '@libs/chess/pgn';
import { cx } from '@libs/classnames';
import { ARRAY_EMPTY } from '@libs/constants';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { indexToAlpha } from '@libs/numbers';
import { scrollToElement } from '@libs/scroll';
import { Subscription } from '@libs/subscription';
import { ContextMenu, ContextMenuItem } from '@ui/components/ContextMenu/ContextMenu';
import { StarsFigure } from '@ui/components/StarsFigure/StarsFigure';
import { UndoControls } from '@ui/components/UndoControls';
import { BinIcon } from '@ui/icons/BinIcon';
import { IconBack, IconFastBack } from '@ui/icons/PgnContols';

import { INagInfo, NAGS, NAGS_COMMON, NAGS_ESSENTIAL, NAGS_OTHER } from './nags';
import { findMoveById, findVariantById } from './services/moves';
import {
  ICommentTarget,
  IGameCommentTarget,
  IModelFullMove,
  IModelHalfMove,
  IModelStub,
  IModelVariant,
  IVariantCommentTarget,
  mapModel,
  ModelCache,
} from './services/viewModel';

import styles from './PgnEditor.module.css';

/*
    ____,-------------------------------,____
    \   |          Компоненты           |   /
    /___|-------------------------------|___\
*/

// TODO: придумать как зачёркивать ходы при ховере корзины, которые будут
// удалены вместе с выбранным

// Навигационное меню по вариантам
// - 'continue' — следующий ход
// - 'switch' — ход альтернативный текущему
export type INavMenuItem = {
  type: 'continue' | 'switch';
  move: IPgnMove;
};

export type Props = {
  className?: string;
  style?: CSSProperties;
  //
  valuator?: ReactNode;
  //
  pgn?: IPgnVariantDto<IPgnMove>;
  meta?: Record<string, IMoveMeta>;
  //
  result?: string | null;
  //
  selected?: string;
  showMenu?: boolean;
  //
  next?: string;
  last?: string;
  prev?: string | null; // null — значит можно перейти на 0 ход (сбросить selected)
  first?: string | null;
  highlighted?: string; // подсвеченная опция cycle (золотой outline)
  navItems?: INavMenuItem[];
  //
  canUndo?: boolean;
  canRedo?: boolean;
  //
  startFullMove?: number;
  startColor?: 'white' | 'black';
  //
  readonlyMainVariant?: boolean;
  showPointsEditor?: boolean;
  showPreludeEditor?: boolean;
  enableGlobalShortcuts?: boolean;
  //
  onMoveSelect?: (id?: string) => void;
  onSelectedBlur?: () => void;
  onMenuOpen?: (id: string) => void;
  onMenuClose?: () => void;
  //
  onMoveDelete?: (m: IPgnMove) => void;
  onVariantPromote?: (m: IPgnMove) => void;
  onVariantMove?: (m: IPgnMove, direction: 'up' | 'down') => void;
  onMoveCommentCreate?: (m: IPgnMove) => void;
  onMoveCommentUpdate?: (comment: string, m: IPgnMove) => void;
  onPreludeCreate?: (m: IGameCommentTarget | IVariantCommentTarget) => void;
  onPreludeUpdate?: (comment: string, m: IGameCommentTarget | IVariantCommentTarget) => void;
  onNagsUpdate?: (nags: number[] | undefined, m: IPgnMove) => void;
  onPointsUpdate?: (points: number | undefined, m: IPgnMove) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onNavMenuCycle?: (direction: 'up' | 'down') => void;
};

type IPgnContext = {
  subscriptionFocus: Subscription<string | undefined>;
  //
  refMenuPortal: MutableRefObject<HTMLDivElement | null>;
  //
  refSelected: MutableRefObject<HTMLDivElement | null>;
  refSelectedMove: MutableRefObject<IPgnMove | undefined>;
  //
  refProps: MutableRefObject<Props>;
};

export type IMoveMeta = {
  score?: IEvaluationScore;
};

type IPgnSelectionContext = {
  selectedMove?: string;
  highlightedMove?: string;
  showMenu?: boolean;
};

const PGN_META_COMMENT_ID = 'pgn-meta';

const PgnContext = createContext<IPgnContext | undefined>(undefined);
const PgnSelectionContext = createContext<IPgnSelectionContext>({});

export function PgnEditor(props: Props) {
  const {
    className,
    style,
    //
    valuator,
    //
    pgn,
    meta,
    //
    result,
    selected,
    showMenu,
    next,
    prev,
    first,
    last,
    highlighted,
    navItems,
    //
    canUndo,
    canRedo,
    //
    startFullMove = 1,
    startColor = 'white',
    //
    readonlyMainVariant: _readonlyMainVariant,
    showPointsEditor: _showPointsEditor,
    showPreludeEditor: _showPreludeEditor,
    enableGlobalShortcuts,
    //
    onMoveSelect: _onMoveSelect,
    onSelectedBlur: _onSelectedBlur,
    onMenuOpen: _onMenuOpen,
    onMenuClose: _onMenuClose,
    onMoveDelete: _onMoveDelete,
    onVariantPromote: _onVariantPromote,
    onVariantMove: _onVariantMove,
    onMoveCommentCreate: _onMoveCommentCreate,
    onMoveCommentUpdate: _onMoveCommentUpdate,
    onPreludeCreate: _onPreludeCreate,
    onPreludeUpdate: _onPreludeUpdate,
    onNagsUpdate: _onNagsUpdate,
    onPointsUpdate: _onPointsUpdate,
    onUndo,
    onRedo,
    onNavMenuCycle: _onNavMenuCycle,
  } = props;

  const refProps = useRef(props);
  refProps.current = props;

  const moves = pgn?.moves ?? ARRAY_EMPTY;
  const subscriptionFocus = useMemo(() => new Subscription<string | undefined>(), []);
  const cache = useMemo(() => new ModelCache(), []);
  const model = useMemo(() => mapModel(moves, pgn, cache), [moves, pgn, cache]);

  const refMenuPortal = useRef<HTMLDivElement>(null);
  const { refScrollable } = useScrollToLatest({
    count: model.count,
    result,
    selected,
    // FIXME: пока отключим, чтобы подумать как подружить с useFocusSelected
    disabled: true,
  });
  const { refSelected } = useFocusSelected(selected, refScrollable);

  const refSelectedMove = useRef<IPgnMove>(undefined);
  if (!selected) {
    refSelectedMove.current = undefined;
  }

  const context = useMemo<IPgnContext>(
    () => ({
      subscriptionFocus,
      refMenuPortal,
      refSelected,
      refSelectedMove,
      refProps,
    }),
    [refSelected, subscriptionFocus],
  );
  const contextSelection = useMemo<IPgnSelectionContext>(
    () => ({ selectedMove: selected, highlightedMove: highlighted, showMenu }),
    [selected, highlighted, showMenu],
  );

  // ----- Global Shortcuts

  useEffect(() => {
    if (!enableGlobalShortcuts) return;

    function focusSelected() {
      refSelected.current?.querySelector('button')?.focus({ preventScroll: true });
    }

    function onKeyboard(e: KeyboardEvent) {
      const s = refProps.current;
      const focused = document.activeElement;
      const hasNavItems = !!s.navItems?.length;

      if (
        focused &&
        ((focused.nodeName === 'INPUT' &&
          !['checkbox', 'radio'].includes(focused.getAttribute('type')!)) ||
          focused.nodeName === 'TEXTAREA' ||
          focused.nodeName === 'SELECT' ||
          focused.getAttribute('contentEditable'))
      )
        return;

      if (e.code === 'ArrowRight' && s.next) {
        s.onMoveSelect?.(s.next);
        e.preventDefault();
      } else if (e.code === 'ArrowLeft' && s.prev !== undefined) {
        s.onMoveSelect?.(s.prev ?? undefined);
        e.preventDefault();
      } else if (e.key === 'Enter' && hasNavItems) {
        if (!s.highlighted) {
          s.onMenuClose?.();
          s.onNavMenuCycle?.('down');
          focusSelected();
        } else {
          s.onMoveSelect?.(s.highlighted);
        }
        e.preventDefault();
      } else if (e.code === 'ArrowDown') {
        if (s.highlighted) {
          s.onNavMenuCycle?.('down');
          focusSelected();
          e.preventDefault();
        } else if (s.last) {
          s.onMoveSelect?.(s.last);
          e.preventDefault();
        }
      } else if (e.code === 'ArrowUp') {
        if (s.highlighted) {
          s.onNavMenuCycle?.('up');
          focusSelected();
          e.preventDefault();
        } else if (s.first !== undefined) {
          s.onMoveSelect?.(s.first ?? undefined);
          e.preventDefault();
        }
      } else if (e.key === 'Escape' && s.highlighted) {
        s.onSelectedBlur?.();
        e.preventDefault();
      } else if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey)) {
        e.shiftKey ? s.onRedo?.() : s.onUndo?.();
        e.preventDefault();
      } else if (e.code === 'Delete' && refSelectedMove.current) {
        const m = refSelectedMove.current;
        const isMainLine = !m.altId;
        if (!s.readonlyMainVariant || !isMainLine) {
          s.onMoveDelete?.(m);
          e.preventDefault();
        }
      } else if (e.code === 'Space') {
        const m = refSelectedMove.current;
        if (m) {
          if (m.comment !== undefined) {
            subscriptionFocus.publish(m.id);
            s.onMenuClose?.();
          } else {
            s.onMoveCommentCreate?.(m);
          }
        } else {
          if (s.pgn?.prelude !== undefined) {
            subscriptionFocus.publish(PGN_META_COMMENT_ID);
          } else {
            s.onPreludeCreate?.({ type: 'main' });
          }
        }
        e.preventDefault();
      }
    }

    window.addEventListener('keydown', onKeyboard);

    return () => window.removeEventListener('keydown', onKeyboard);
  }, [enableGlobalShortcuts, refSelected, subscriptionFocus]);

  // -----

  const gameCommentTarget = useMemo<IGameCommentTarget | undefined>(() => {
    return model.comment !== undefined ? { type: 'main', comment: model.comment } : undefined;
  }, [model.comment]);

  return (
    <PgnContext.Provider value={context}>
      <PgnSelectionContext.Provider value={contextSelection}>
        <section className={cx(styles.pgn, className)} style={style}>
          {valuator && <div className={styles.pgn__valuator}>{valuator}</div>}
          <div className={styles.pgn__controls}>
            <Controls count={model.count} next={next} prev={prev} first={first} last={last} />
          </div>
          <div className={styles.pgn__moves} ref={refScrollable}>
            <div className={styles.pgn__moves__portal} ref={refMenuPortal} />
            <div className={styles.pgn__moves__clip}>
              {gameCommentTarget && <BlockComment move={gameCommentTarget} />}
              {model.moves.map((fm, i) => (
                <FullMove
                  key={i}
                  move={fm}
                  isFirst={i === 0}
                  hasGamePrelude={i === 0 ? model.comment !== undefined : undefined}
                  metaWhite={findMeta(fm.white, meta)}
                  metaBlack={findMeta(fm.black, meta)}
                />
              ))}
              {model.moves.length === 0 && (
                <EmptyFullMove color={startColor} fullMove={startFullMove} />
              )}
              {result && <div className={styles.result}>{result}</div>}
            </div>
            {highlighted && !!navItems?.length && (
              <NavMenuMenu items={navItems} highlighted={highlighted} />
            )}
          </div>
          {(canUndo || canRedo) && (
            <UndoControls
              className={styles.pgn__undo}
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={onUndo}
              onRedo={onRedo}
            />
          )}
        </section>
      </PgnSelectionContext.Provider>
    </PgnContext.Provider>
  );
}

const Controls = memo(function Controls({
  count,
  next,
  prev,
  first,
  last,
}: {
  count: number;
  next?: string;
  last?: string;
  prev?: string | null; // null — значит можно перейти на 0 ход (сбросить selected)
  first?: string | null;
}) {
  const { onMoveSelect } = usePgnContext().refProps.current;
  return (
    <ul className={styles.controls}>
      <li className={cx(styles.controls__btn, styles.is_left)}>
        <button disabled={first === undefined} onClick={() => onMoveSelect?.(first ?? undefined)}>
          <IconFastBack />
        </button>
      </li>
      <li className={cx(styles.controls__btn, styles.is_left)}>
        <button
          className={styles.red}
          disabled={prev === undefined}
          onClick={() => onMoveSelect?.(prev ?? undefined)}
        >
          <IconBack />
        </button>
      </li>
      <li className={styles.controls__counter}>{count}</li>
      <li className={cx(styles.controls__btn, styles.is_right)}>
        <button disabled={!next} onClick={() => onMoveSelect?.(next)}>
          <IconBack />
        </button>
      </li>
      <li className={cx(styles.controls__btn, styles.is_right)}>
        <button disabled={!last} onClick={() => onMoveSelect?.(last)}>
          <IconFastBack />
        </button>
      </li>
    </ul>
  );
});

const FullMove = memo(function FullMove({
  move,
  isFirst,
  hasGamePrelude,
  metaWhite,
  metaBlack,
}: {
  move: IModelFullMove;
  isFirst?: boolean;
  hasGamePrelude?: boolean;
  metaWhite?: IMoveMeta;
  metaBlack?: IMoveMeta;
}) {
  const { white, black, branches } = move;
  const commentedHalfMove = (() => {
    if (white.type === 'move' && white.move.comment !== undefined) return white.move;
    if (black?.type === 'move' && black.move.comment !== undefined) return black.move;
  })();
  const counter = white?.move.moveFull ?? black?.move.moveFull;

  const isFirstWhite = isFirst && white.type === 'move';
  const isFirstBlack = isFirst && white.type === 'stub';

  return (
    <div className={styles.full_move}>
      <div className={styles.full_move__moves}>
        <div className={styles.full_move__moves__counter}>{counter}</div>
        <div className={styles.full_move__moves__half_move}>
          <HalfMoveConnected
            move={white}
            noNumber
            isFirstMove={isFirstWhite}
            preludeId={isFirstWhite ? PGN_META_COMMENT_ID : undefined}
            hasPrelude={isFirstWhite ? hasGamePrelude : undefined}
          />
          <HalfMoveAttributes move={white} meta={metaWhite} />
        </div>
        <div className={styles.full_move__moves__half_move}>
          {black && (
            <HalfMoveConnected
              move={black}
              noNumber
              isFirstMove={isFirstBlack}
              preludeId={isFirstBlack ? PGN_META_COMMENT_ID : undefined}
              hasPrelude={isFirstBlack ? hasGamePrelude : undefined}
            />
          )}
          {black && <HalfMoveAttributes move={black} meta={metaBlack} />}
        </div>
      </div>
      {commentedHalfMove && <BlockComment move={commentedHalfMove} />}
      {branches && (
        <div className={styles.full_move__alts}>
          {branches.map((a, i) => (
            <Variant key={i} variant={a} label={indexToAlpha(i)} />
          ))}
        </div>
      )}
    </div>
  );
});

const EmptyFullMove = memo(function EmptyFullMove({
  color,
  fullMove,
}: {
  color: 'white' | 'black';
  fullMove: number;
}) {
  return (
    <div className={styles.full_move}>
      <div className={styles.full_move__moves}>
        <div className={styles.full_move__moves__counter}>{fullMove}.</div>
        <div className={styles.full_move__moves__half_move}>{color === 'white' ? '' : '...'}</div>
        <div className={styles.full_move__moves__half_move} />
      </div>
    </div>
  );
});

function BlockComment({ move }: { move: ICommentTarget }) {
  return (
    <div className={styles.block_comment}>
      <EditableCommentText target={move} multiline />
    </div>
  );
}

// Вынесен в отдельный компонент, чтобы обрабатывать контекст (selected и menu)
function HalfMoveConnected({
  className,
  move,
  noNumber,
  isFirstMove,
  preludeId,
  hasPrelude,
}: {
  className?: string;
  move: IModelHalfMove | IModelStub;
  noNumber?: boolean;
  isFirstMove?: boolean;
  // имеет смысл только для isFirstMove
  preludeId?: string;
  hasPrelude?: boolean;
}) {
  const halfmove = move.type === 'move' ? move.move : undefined;
  const { selectedMove, highlightedMove, showMenu } = useContext(PgnSelectionContext);
  const selected = Boolean(halfmove && halfmove.id === selectedMove);
  const highlighted = Boolean(halfmove && halfmove.id === highlightedMove);
  const isMenuOpen = Boolean(selected && showMenu);

  return move.type === 'move' ? (
    <HalfMove
      className={className}
      move={move}
      noNumber={noNumber}
      selected={selected}
      highlighted={highlighted}
      isMenuOpen={isMenuOpen}
      isFirstMove={isFirstMove}
      preludeId={preludeId}
      hasPrelude={hasPrelude}
    />
  ) : (
    <HalfMoveStub className={className} move={move} noNumber={noNumber} />
  );
}

const HalfMove = memo(function HalfMove({
  className,
  move,
  noNumber,
  selected,
  highlighted,
  isMenuOpen,
  isFirstMove,
  preludeId,
  hasPrelude,
}: {
  className?: string;
  move: IModelHalfMove;
  noNumber?: boolean;
  selected: boolean;
  highlighted: boolean;
  isMenuOpen: boolean;
  isFirstMove?: boolean;
  preludeId?: string;
  hasPrelude?: boolean;
}) {
  const halfmove = move.move;
  const number = noNumber || move.move.color === 'black' ? '' : move.move.moveFull;

  const { refSelected, refProps, refSelectedMove } = usePgnContext();
  const { onSelectedBlur, onMoveSelect, onMenuOpen } = refProps.current;

  if (selected) {
    refSelectedMove.current = move.move;
  }

  const refSan = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selected)
      refSan.current?.focus({
        preventScroll: true,
      });
  }, [selected]);

  const customColor = halfmove.nags?.find((n) => n >= 1 && n <= 6);
  const [prefixes, suffixes] = splitNags(halfmove.nags);

  return (
    <div
      className={cx(styles.half_move, className, {
        [styles.is_white]: move.move.color === 'white',
        [styles.is_black]: move.move.color === 'black',
        [styles.is_selected]: selected,
        [styles.is_highlighted]: highlighted,
        [styles.is_menu_open]: isMenuOpen,
      })}
      ref={selected ? refSelected : undefined}
    >
      {number && <span className={styles.half_move__counter}>{number}</span>}
      {/* NAGs-префиксы */}
      {!!prefixes?.length && (
        <NagsList
          className={styles.half_move__nags_prefix}
          nags={prefixes}
          onPointerDown={isMenuOpen ? undefined : () => onMenuOpen?.(halfmove.id)}
        />
      )}
      {/* SAN */}
      <button
        ref={refSan}
        className={cx(styles.half_move__san, customColor ? styles[`is_${customColor}`] : null)}
        onFocus={() => {
          if (selected) return;
          onMoveSelect?.(halfmove.id);
        }}
        // sic! используем pointerdown вместо click, т.к. он отработает до focus
        onPointerDown={(e) => {
          // игнорируем правую кнопку, за неё отвечает contextmenu
          if (e.button !== 0) {
            return;
          }

          if (selected && !isMenuOpen) {
            onMenuOpen?.(halfmove.id);
          } else {
            onMoveSelect?.(halfmove.id);
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          onMenuOpen?.(halfmove.id);
        }}
        onBlur={selected ? onSelectedBlur : undefined}
      >
        <span>{move.move.san === '--' ? '—' : move.move.san}</span>
      </button>

      {/* NAGs */}
      {(!!suffixes?.length || halfmove.points !== undefined) && (
        <NagsList
          className={styles.half_move__nags}
          nags={suffixes ?? ARRAY_EMPTY}
          points={halfmove.points}
          onPointerDown={isMenuOpen ? undefined : () => onMenuOpen?.(halfmove.id)}
        />
      )}

      {/* Arrows indicator */}
      {halfmove?.coloredArrows && (
        <IconMenuArrow className={styles.half_move__arrows_indicator} direction="diag" />
      )}
      {/* Squares indicator */}
      {halfmove?.coloredSquares && !halfmove?.coloredArrows && (
        <span className={styles.half_move__circles_indicator} />
      )}

      {/* Menu */}
      {isMenuOpen && (
        <MoveMenuPositioner refNode={refSan}>
          <MoveMenu
            move={halfmove}
            isFirstMove={isFirstMove}
            preludeId={preludeId}
            hasPrelude={hasPrelude}
          />
        </MoveMenuPositioner>
      )}
    </div>
  );
});

const HalfMoveStub = memo(function HalfMoveStub({
  className,
  move,
  noNumber,
}: {
  className?: string;
  move: IModelStub;
  noNumber?: boolean;
}) {
  const number = noNumber || move.move.color === 'black' ? '' : move.move.moveFull;

  return (
    <div
      className={cx(styles.half_move, className, {
        [styles.is_white]: move.move.color === 'white',
        [styles.is_black]: move.move.color === 'black',
        [styles.is_no_pointer]: true,
        [styles.is_stub]: true,
      })}
    >
      {number && <span className={styles.half_move__counter}>{number}</span>}
      <button tabIndex={-1} className={styles.half_move__san}>
        <span>...</span>
      </button>
    </div>
  );
});

function NagsList({
  className,
  nags,
  points,
  onPointerDown,
}: {
  className?: string;
  nags: number[];
  points?: number;
  onPointerDown?: () => void;
}) {
  return (
    <div className={cx(styles.nags_list, className)} onPointerDown={onPointerDown}>
      {nags.map((n, i) => (
        <Nag key={`${i}-${n}`} nag={n} />
      ))}
      {points !== undefined && <StarsFigure className={styles.nags_list__stars} value={points} />}
    </div>
  );
}

// TODO: превратить все символы из меню в svg и добавить title
export function Nag({ className, nag }: { className?: string; nag: number }) {
  const info = NAGS[nag];

  if (!info) return null;

  return (
    <span
      className={cx(styles.nag, className, styles[`is_${nag}`], {
        [styles.is_two_char]: !info.path && info.symbol.length === 2,
        [styles.is_prefix]: info.category === 'prefix',
        [styles.is_path]: info.path,
      })}
      title={info.desc}
    >
      {info.path && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          width="16"
          height="16"
          viewBox="0 0 16 16"
        >
          <path fill="currentColor" d={info.path} />
        </svg>
      )}
      <span>{info.symbol}</span>
    </span>
  );
}

function HalfMoveAttributes({
  move,
  meta,
}: {
  move: IModelHalfMove | IModelStub;
  meta?: IMoveMeta;
}) {
  if (move.type === 'stub') return null;

  const m = move.move;
  const entries: string[] = [];

  const score = meta?.score || m.score;
  if (score) {
    entries.push(formatDecimalValuation(score.type, score.value, 1));
  }

  if (m.timeElapsed !== undefined) {
    entries.push(`${m.timeElapsed}c`);
  }

  return (
    <span
      className={cx(styles.half_move_attributes, {
        [styles.is_white]: move.move.color === 'white',
        [styles.is_black]: move.move.color === 'black',
      })}
    >
      {entries.map((e, i) => (
        <span key={i}>{e}</span>
      ))}
    </span>
  );
}

const Variant = memo(function Variant({
  label,
  variant,
  inline,
  trailingClose,
  isLastEntryInParent,
}: {
  label?: string;
  variant: IModelVariant;
  inline?: boolean;
  trailingClose?: string;
  isLastEntryInParent?: boolean;
}) {
  const brackets = variant.virtual ? '… ' : variant.level % 2 === 1 ? '[]' : '()';
  const [folded, setFolded] = useState(false);

  const closingBracket = variant.virtual ? trailingClose : label ? undefined : brackets[1];
  const deferClosingToFirstBranch = variant.branches?.[0]?.virtual;
  const renderedClose = deferClosingToFirstBranch ? undefined : closingBracket;
  const isTree = !!variant.branches?.length;

  if (folded)
    return (
      <div
        className={cx(styles.variant, {
          [styles.is_inline]: inline,
          [styles.is_labeled]: label,
          [styles.is_lined]: !label && !inline,
        })}
        onClick={() => setFolded((f) => !f)}
      >
        {label && <span className={styles.variant__label}>{label}</span>}
        <div className={styles.variant__folded}>
          {brackets[0]}+{closingBracket ?? brackets[1]}
        </div>
        <span className={cx(styles.separator, { [styles.is_long]: !isLastEntryInParent })}> </span>
      </div>
    );

  return (
    <div
      className={cx(styles.variant, {
        [styles.is_inline]: inline,
        [styles.is_labeled]: label,
        [styles.is_lined]: !label && !inline,
        [styles.is_tree]: isTree,
      })}
    >
      <span className={styles.variant__label} onClick={() => setFolded((f) => !f)}>
        {label ? label : brackets[0]}
      </span>
      <div className={styles.variant__blocks}>
        {variant.entries.map((e, i) => {
          if (e.type === 'block') {
            return (
              <div key={i} className={styles.variant__blocks__block}>
                {i === 0 && variant.comment !== undefined && (
                  <>
                    <span className={styles.variant__blocks__block__comment}>
                      <EditableCommentText target={variant} multiline />
                    </span>{' '}
                  </>
                )}
                {e.moves.map((m, j) => {
                  const isFirstHalfmoveInVariant =
                    i === 0 && (j === 0 || (j === 1 && e.moves[0]?.type === 'stub'));
                  const isLongSeparatorNeeded =
                    m.type === 'move' &&
                    !m.move.alts &&
                    m.move.color === 'black' &&
                    j !== e.moves.length - 1;
                  return (
                    <Fragment key={j}>
                      <HalfMoveConnected
                        className={styles.variant__blocks__block__halfmove}
                        move={m}
                        isFirstMove={isFirstHalfmoveInVariant}
                        preludeId={
                          isFirstHalfmoveInVariant && !variant.virtual
                            ? variant.variantId
                            : undefined
                        }
                        hasPrelude={
                          isFirstHalfmoveInVariant && !variant.virtual
                            ? variant.comment !== undefined
                            : undefined
                        }
                      />
                      <span
                        className={cx(styles.separator, {
                          [styles.is_long]: isLongSeparatorNeeded && !m.move.comment,
                          [styles.is_short]: m.type === 'move' && m.move.comment !== undefined,
                        })}
                      >
                        {' '}
                      </span>
                      {m.type === 'move' && m.move.comment !== undefined && (
                        <>
                          <span className={styles.variant__blocks__block__comment}>
                            <EditableCommentText target={m.move} multiline />
                          </span>
                          <span
                            className={cx(styles.separator, {
                              [styles.is_long]: isLongSeparatorNeeded,
                            })}
                          >
                            {' '}
                          </span>
                        </>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            );
          } else if (e.type === 'variant') {
            return (
              <Variant
                key={i}
                variant={e}
                inline
                isLastEntryInParent={i === variant.entries.length - 1}
              />
            );
          }
        })}

        {!label && renderedClose !== undefined && (
          <>
            <span
              className={cx(styles.variant__label, styles.is_closing)}
              onClick={() => setFolded((f) => !f)}
            >
              {renderedClose}
            </span>
            {!isTree && (
              <span className={cx(styles.separator, { [styles.is_long]: !isLastEntryInParent })}>
                {' '}
              </span>
            )}
          </>
        )}
        {variant.branches?.map((b, i) => (
          <Variant
            key={i}
            variant={b}
            trailingClose={i === 0 && b.virtual ? closingBracket : undefined}
          />
        ))}
      </div>
    </div>
  );
});

// TODO: safari (иногда) криво ставит фокус в новый (пустой) коммент
function EditableCommentText({
  target,
  multiline,
}: {
  target: ICommentTarget;
  multiline?: boolean;
}) {
  const { subscriptionFocus, refProps } = usePgnContext();
  const { onMoveCommentUpdate, onPreludeUpdate } = refProps.current;

  const isMove = target.type === undefined;
  const canEdit = isMove ? !!onMoveCommentUpdate : !!onPreludeUpdate;

  const onChange = useCallback(
    (text: string) => {
      const c = sanitizeComment(text, multiline);

      // sync DOM
      const el = ref.current;
      if (c !== text && el) {
        el.innerText = c;
      }

      if (isMove) {
        onMoveCommentUpdate?.(c, target);
      } else {
        onPreludeUpdate?.(c, target);
      }
    },
    [multiline, isMove, onMoveCommentUpdate, target, onPreludeUpdate],
  );

  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !canEdit) return;

    // Подписка на focus
    const unsubscribeFocus = subscriptionFocus.subscribe((m) => {
      if (
        (target.type === 'main' && m !== PGN_META_COMMENT_ID) ||
        (target.type === 'variant' && m !== target.variantId) ||
        (target.type === undefined && target.id !== m)
      ) {
        return;
      }

      el.focus();
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      sel?.removeAllRanges();
      sel?.addRange(range);
    });

    // Режим редактирования

    try {
      el.contentEditable = 'plaintext-only';
    } catch {
      // sic! workaround для firefox, который не поддерживает 'plaintext-only'
      el.contentEditable = 'true';
    }

    // Автофокус на новый (пустой) комментарий

    if (!el.innerText.trim()) {
      el.focus();
    }

    // События

    // - блокировка перевода строки + Escape
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (!multiline || !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          el.blur();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        window.getSelection()?.removeAllRanges();
        el.blur();
      }
    };
    el.addEventListener('keydown', onKeyDown);

    // - санитайзинг буфера обмена
    const onPaste = (e: ClipboardEvent) => {
      const data = e.clipboardData?.getData('text/plain');
      if (!data) return;

      const sanitized = sanitizeComment(data, multiline);

      if (sanitized !== data) {
        e.preventDefault();
        e.stopPropagation();
        const selection = window.getSelection();
        if (selection?.rangeCount) {
          selection.deleteFromDocument();
          selection.getRangeAt(0).insertNode(document.createTextNode(sanitized));
          selection.collapseToEnd();
        }
      }
    };
    el.addEventListener('paste', onPaste);

    return () => {
      unsubscribeFocus();
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('paste', onPaste);
    };
  }, [target, multiline, canEdit, subscriptionFocus]);

  return (
    <span
      ref={ref}
      className={styles.editable_comment_text}
      onBlur={(e) => onChange(e.target.innerText)}
    >
      {target.comment}
    </span>
  );
}

function NavMenuMenu({ items, highlighted }: { items: INavMenuItem[]; highlighted: string }) {
  const { refSelected, refMenuPortal, refProps } = usePgnContext();

  const floating = useFloating({
    // placement: 'bottom-start',
    placement: 'top-start',
    whileElementsMounted: autoUpdate,
    middleware: [flip()],
  });

  useEffect(() => {
    if (refSelected.current) floating.refs.setReference(refSelected.current);
  }, [floating.refs, refSelected]);

  if (!refMenuPortal.current) return null;

  return createPortal(
    <ContextMenu
      className={cx(styles.alt_cycle_menu, styles[floating.placement])}
      animated
      ref={floating.refs.setFloating}
      style={{
        ...floating.floatingStyles,
        zIndex: 3,
        visibility: floating.isPositioned ? 'visible' : 'hidden',
      }}
    >
      {items.map((item) => {
        const isSelected = highlighted === item.move.id;
        const [prefixes, suffixes] = splitNags(item.move.nags);
        return (
          <ContextMenuItem
            key={item.move.id}
            textClassName={styles.alt_cycle_menu__item}
            highlighted={isSelected}
            hover="light"
            onMouseDown={() => refProps.current.onMoveSelect?.(item.move.id)}
          >
            <span
              className={cx(styles.alt_cycle_menu__item__san, {
                [styles.is_white]: item.move.color === 'white',
                [styles.is_black]: item.move.color === 'black',
              })}
            >
              <span>{item.move.moveFull}</span>
              {!!prefixes?.length && (
                <NagsList className={styles.half_move__nags_prefix} nags={prefixes} />
              )}
              {item.move.san}
            </span>
            {!!suffixes?.length && <NagsList className={styles.half_move__nags} nags={suffixes} />}
          </ContextMenuItem>
        );
      })}
    </ContextMenu>,
    refMenuPortal.current,
  );
}

// Вынесен как отдельный элемент, чтобы ход и меню не перерисовывались лишний
// раз при сролле
function MoveMenuPositioner({
  children,
  refNode,
}: {
  children: ReactNode;
  refNode: MutableRefObject<HTMLButtonElement | null>;
}) {
  const floating = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [flip()],
  });

  const { refMenuPortal } = usePgnContext();
  useEffect(() => {
    if (refNode.current) floating.refs.setReference(refNode.current);
  }, [floating.refs, refNode]);

  return (
    refMenuPortal.current &&
    createPortal(
      <div
        className={cx(styles.move_menu_positioner, styles[floating.placement])}
        ref={floating.refs.setFloating}
        style={{
          ...floating.floatingStyles,
          zIndex: 3,
          // workaround для случаев с дёрганием при первом открытии (случается редко)
          visibility: floating.isPositioned ? 'visible' : 'hidden',
        }}
      >
        {/*
          TODO: тут отлично бы смотрелась анимация появления со scale из угла (но сложно при
                этом исключить из анимации SAN хода)
        */}
        {children}
      </div>,
      refMenuPortal.current,
    )
  );
}

const MoveMenu = memo(function MoveMenu({
  move,
  isFirstMove,
  preludeId,
  hasPrelude,
}: {
  move: IPgnMove;
  isFirstMove?: boolean;
  preludeId?: string;
  hasPrelude?: boolean;
}) {
  const { subscriptionFocus, refProps } = usePgnContext();
  const {
    readonlyMainVariant,
    showPointsEditor,
    showPreludeEditor,
    onMoveDelete,
    onMoveCommentCreate,
    onPreludeCreate,
    onMenuClose,
    onNagsUpdate,
    onPointsUpdate,
    onVariantPromote,
    onVariantMove,
  } = refProps.current;

  const ref = useClickOutside<HTMLDivElement>(onMenuClose);

  const moveNags = useMemo(
    () =>
      move.nags?.map((n) => NAGS[n]?.sameAs ?? NAGS[n]?.num).filter(Boolean) as
        | number[]
        | undefined,
    [move.nags],
  );
  const nags = useMemo(() => {
    const s = new Set([...NAGS_ESSENTIAL, ...NAGS_COMMON]);
    // добавим недостающие из хода
    moveNags?.forEach((n) => s.add(n));
    return Array.from(s);
  }, [moveNags]);
  const onNagClick = (info: INagInfo, selected?: boolean) => {
    if (selected) {
      onNagsUpdate?.(
        moveNags?.filter((x) => x !== info.num),
        move,
      );
    } else {
      const filtered = moveNags?.filter((x) => NAGS[x]?.category !== info.category) ?? [];

      onNagsUpdate?.(
        info.category === 'essential' ? [info.num, ...filtered] : [...filtered, info.num],
        move,
      );
    }
  };

  const isNullMove = move.san === '--';
  const isMainLine = !move.altId;
  const moves = refProps.current.pgn?.moves;

  const variant = !isMainLine && moves ? findVariantById(move.altId!, moves) : undefined;
  const variantParent = variant && moves ? findMoveById(variant.parentMoveId, moves) : undefined;
  const variantIdx = variantParent?.alts?.indexOf(variant!) ?? -1;
  const variantsCount = variantParent?.alts?.length ?? 0;

  const canPromoteVariant = !!variant && (!readonlyMainVariant || !!variantParent?.altId);
  const canMoveVariantUp = variantIdx > 0;
  const canMoveVariantDown = variantIdx >= 0 && variantIdx < variantsCount - 1;

  const showActions = canPromoteVariant || canMoveVariantUp || canMoveVariantDown;
  const customColor = move.nags?.find((n) => n >= 1 && n <= 6);

  const [isOtherOpen, setOtherOpen] = useState(false);

  return (
    <div
      ref={ref}
      className={cx(styles.move_menu, {
        [styles.is_white]: move.color === 'white',
        [styles.is_black]: move.color === 'black',
        [styles.is_wider]: customColor,
      })}
    >
      <div className={styles.move_menu__head}>
        <button
          className={cx(styles.move_menu__head__san, styles.half_move__san)}
          // sic! pointerdown вместо click чтобы не конфликтовать с нажатием на
          // ход (иначе меню откроется и тут же закроется ибо одно нажатие
          // породит два события на разных элементах)
          onPointerDown={() => {
            // sic! raf чтобы не конфликтовать с contextmenu хода (иначе правая кнопка
            // закроет и тут же пререоткроет меню)
            requestAnimationFrame(() => onMenuClose?.());
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            onMenuClose?.();
          }}
        >
          <span>{isNullMove ? '—' : move.san}</span>
        </button>
        <span className={styles.move_menu__head__icons}>
          {isFirstMove && preludeId && showPreludeEditor && (
            <IconComment
              className={styles.move_menu__prelude}
              onClick={() => {
                if (hasPrelude) {
                  subscriptionFocus.publish(preludeId);
                  onMenuClose?.();
                } else if (preludeId === PGN_META_COMMENT_ID) {
                  onPreludeCreate?.({ type: 'main' });
                } else {
                  onPreludeCreate?.({
                    type: 'variant',
                    variantId: preludeId,
                  });
                }
              }}
            />
          )}
          {!isNullMove && (
            <IconComment
              className={styles.move_menu__comment}
              onClick={() => {
                if (move.comment) {
                  subscriptionFocus.publish(move.id);
                  onMenuClose?.();
                } else {
                  onMoveCommentCreate?.(move);
                }
              }}
            />
          )}
          {(!readonlyMainVariant || !isMainLine) && (
            <BinIcon className={styles.move_menu__delete} onClick={() => onMoveDelete?.(move)} />
          )}
        </span>
      </div>
      <div className={styles.move_menu__content}>
        {!isNullMove && (
          <div className={styles.move_menu__nags}>
            {nags.map((n) => {
              const info = NAGS[n];
              const selected = moveNags?.includes(n);
              if (!info) return null;

              return (
                <span
                  key={n}
                  className={cx(styles.move_menu__nags__nag, {
                    [styles.is_selected]: selected,
                  })}
                  onClick={() => onNagClick(info, selected)}
                >
                  <Nag nag={n} />
                </span>
              );
            })}
          </div>
        )}

        {showPointsEditor && (
          <div className={styles.move_menu__stars}>
            {[1, 2, 3].map((n) => {
              const selected = move.points === n;
              return (
                <div
                  key={n}
                  className={cx(styles.move_menu__stars__item, {
                    [styles.is_selected]: selected,
                  })}
                  onClick={() => onPointsUpdate?.(selected ? undefined : n, move)}
                >
                  <StarsFigure value={n} />
                </div>
              );
            })}
          </div>
        )}

        {isOtherOpen && (
          <div className={styles.move_menu__nags}>
            {NAGS_OTHER.map((n) => {
              const selected = moveNags?.includes(n);

              const info = NAGS[n];
              if (selected || !info) return null;

              return (
                <span
                  key={n}
                  className={cx(styles.move_menu__nags__nag, {
                    [styles.is_selected]: selected,
                  })}
                  onClick={() => onNagClick(info, selected)}
                >
                  <Nag nag={n} />
                </span>
              );
            })}
          </div>
        )}
        <div
          className={styles.move_menu__other_symbols__toggle}
          onClick={() => setOtherOpen((v) => !v)}
        >
          {isOtherOpen ? 'Скрыть дополнительные' : 'Другие символы'}
        </div>

        {showActions && (
          <>
            <hr />
            <div className={styles.move_menu__actions}>
              {canPromoteVariant && (
                <div
                  className={styles.move_menu__actions__item}
                  onClick={() => onVariantPromote?.(move)}
                >
                  <span>Продвинуть вариант</span>
                  <span>
                    <IconMenuArrow direction="diag" />
                  </span>
                </div>
              )}
              {canMoveVariantUp && (
                <div
                  className={styles.move_menu__actions__item}
                  onClick={() => onVariantMove?.(move, 'up')}
                >
                  <span>Повысить вариант</span>
                  <IconMenuArrow direction="up" />
                </div>
              )}
              {canMoveVariantDown && (
                <div
                  className={styles.move_menu__actions__item}
                  onClick={() => onVariantMove?.(move, 'down')}
                >
                  <span>Понизить вариант</span>
                  <IconMenuArrow direction="down" />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

/*
    ____,-------------------------------,____
    \   |             Иконки            |   /
    /___|-------------------------------|___\
*/

function IconComment({ className, onClick }: { className?: string; onClick?: () => void }) {
  return (
    <svg
      className={className}
      onClick={onClick}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 16 15"
      width="16"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M.008 6.996c0-3.843 3.59-6.968 8-6.968l.002.004c4.41 0 8 3.125 8 6.968s-3.59 6.968-8 6.968c-.87 0-1.724-.121-2.536-.356l-.124-.036-1.314.986a2.195 2.195 0 0 1-3.51-1.755v-.068c0-.207.028-.41.083-.607l.46-1.657-.062-.098a6.3 6.3 0 0 1-1-3.381ZM5.33 11.93a7.466 7.466 0 0 0 2.677.485l-.002.003c3.558 0 6.451-2.431 6.451-5.42 0-2.987-2.893-5.419-6.451-5.419-3.559 0-6.452 2.432-6.452 5.42 0 1.03.346 2.03.996 2.895a.68.68 0 0 1 .12.599l-.571 2.054a.675.675 0 0 0-.027.19v.068a.646.646 0 0 0 1.033.516L4.865 12a.499.499 0 0 1 .466-.07Zm3.45-5.711h1.55c.425 0 .773.348.773.774a.777.777 0 0 1-.774.774H8.782v1.548a.777.777 0 0 1-.774.774.777.777 0 0 1-.775-.774V7.767H5.685a.777.777 0 0 1-.774-.774c0-.426.349-.774.774-.774h1.548V4.67c0-.425.35-.774.775-.774.425 0 .774.349.774.774V6.22Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IconMenuArrow({
  className,
  direction,
}: {
  className?: string;
  direction: 'up' | 'down' | 'diag';
}) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="11"
      fill="none"
      viewBox="0 0 10 11"
      style={{
        transform: `rotate(${direction === 'down' ? '180deg' : direction === 'diag' ? '45deg' : 0})`,
      }}
    >
      <path
        fill="currentColor"
        d="M4.197 10.973V2.216L.831 5.593 0 4.773 4.783 0l4.773 4.773-.81.82L5.37 2.216v8.757z"
      />
    </svg>
  );
}

/*
    ____,-------------------------------,____
    \   |              Hooks            |   /
    /___|-------------------------------|___\
*/

function usePgnContext() {
  return useContext(PgnContext)!;
}

function useScrollToLatest({
  count,
  result,
  selected,
  disabled = false,
}: {
  count: number;
  result?: string | null;
  selected?: string;
  disabled?: boolean;
}) {
  const refScrollable = useRef<HTMLDivElement>(null);

  const _store = useRef({ count, result, selected, disabled, rendered: false });
  _store.current.selected = selected;
  _store.current.disabled = disabled;

  useLayoutEffect(
    () => {
      const store = _store.current;

      const mustScroll =
        !store.disabled &&
        refScrollable.current &&
        !store.selected &&
        (count !== store.count || result !== store.result);

      if (mustScroll) {
        refScrollable.current!.scrollTo({
          top: refScrollable.current!.scrollHeight,
          behavior: store.rendered ? 'smooth' : 'instant',
        });
      }

      _store.current = { ...store, count, result, rendered: true };
    },
    /* Скроллим при изменении кол-ва ходов или изменении результата */
    [count, result],
  );

  return { refScrollable };
}

function useFocusSelected(
  selected: string | undefined,
  refScrollable: MutableRefObject<HTMLDivElement | null>,
  { skipEmpty = false }: { skipEmpty?: boolean } = {},
) {
  const refMounted = useRef(false);

  const refSelected = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wasMounted = refMounted.current;

    const t = setTimeout(() => {
      if (!refScrollable.current) return;

      if (!refSelected.current) {
        if (skipEmpty) return;
        refScrollable.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else if (wasMounted) {
        scrollToElement({
          container: refScrollable.current,
          element: refSelected.current,
          offset: 52,
          behavior: 'smooth',
        });
      } else {
        refScrollable.current.scrollTop = refScrollable.current.scrollHeight;
      }
    }, 0);
    return () => clearTimeout(t);
  }, [refScrollable, selected, skipEmpty]);

  useEffect(() => {
    refMounted.current = true;
  }, []);

  return {
    refSelected,
  };
}

/*
    ____,-------------------------------,____
    \   |             Utils             |   /
    /___|-------------------------------|___\
*/

function findMeta(m?: IModelHalfMove | IModelStub, meta?: Record<string, IMoveMeta>) {
  if (!m || !meta || m.type === 'stub') return undefined;

  return meta[m.move.id];
}

function sanitizeComment(data: string, multiline?: boolean) {
  return multiline
    ? data.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n')
    : data.replaceAll(/[\r\n]/g, '');
}

// Разделяет наги на префиксные (рендерятся до SAN) и остальные (после SAN)
function splitNags(
  nags: number[] | undefined,
): [prefix: number[] | undefined, suffix: number[] | undefined] {
  if (!nags?.length) return ARRAY_EMPTY as [undefined, undefined];

  // sic! префикс может быть только 1 (остальные игнорируем)
  const prefix = nags.find((n) => NAGS[n]?.category === 'prefix');

  return prefix
    ? [[prefix], nags.filter((n) => NAGS[n]?.category !== 'prefix')]
    : [undefined, nags];
}
